import {Component, OnInit, ViewChild} from '@angular/core';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {LoadingComponent} from '../../../shared/components/loading/loading.component';
import {FormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {ApplicationSetting} from '../../../core/models/application-settings/application-settings.model';
import {ApplicationSettingsService} from '../../../core/services/application-settings/application-settings.service';
import {AlertComponent} from '../../../shared/components/alert/alert.component';
import {ButtonComponent} from '../../../shared/components/button/button.component';
import {BadgeComponent} from '../../../shared/components/badge/badge.component';
import {ModalComponent} from '../../../shared/components/modal/modal.component';
import {AuthenticationService} from '../../../core/services/authentication/authentication.service';

@Component({
  selector: 'app-application-settings',
  imports: [CommonModule, FormsModule, LoadingComponent, TranslateModule, AlertComponent, ButtonComponent, BadgeComponent, ModalComponent],
  templateUrl: './application-settings.component.html',
  styleUrl: './application-settings.component.scss'
})
export class ApplicationSettingsComponent  implements OnInit {
  settings: ApplicationSetting[] = [];
  originalSettings: ApplicationSetting[] = [];
  changedSettings: ApplicationSetting[] = [];
  loading = false;
  error: string | null = null;
  success: string | null = null;
  editingKeys: Set<string> = new Set();

  @ViewChild('changeSecretConfirmationModal') confirmModal!: ModalComponent;

  constructor(
    private readonly settingsService: ApplicationSettingsService,
    private readonly translateService: TranslateService,
    private readonly authService: AuthenticationService
  ) {}

  get isSuperAdministrator() {
    return this.authService.isSuperAdministrator();
  }

  ngOnInit(): void {
    this.loadSettings();
  }

  loadSettings(): void {
    this.loading = true;
    this.error = null;
    this.success = null;
    this.changedSettings = [];
    this.editingKeys.clear();

    this.settingsService.getSettings().subscribe({
      next: (data) => {
        this.settings = data.map(setting => ({ ...setting }));
        this.originalSettings = data.map(setting => ({ ...setting }));
        this.loading = false;
      },
      error: (err) => {
        this.error = this.translateService.instant('APPLICATION_SETTINGS.MESSAGES.LOAD_ERROR');
        this.loading = false;
        console.error(err);
      }
    });
  }

  startEditing(setting: ApplicationSetting): void {
    // For sensitive data, we need to fetch the actual value
    if (!this.editingKeys.has(setting.key)) {
      this.editingKeys.add(setting.key);
    }
  }

  onSettingChange(setting: ApplicationSetting): void {
    // Check if this setting has changed from its original value
    const originalSetting = this.originalSettings.find(s => s.key === setting.key);
    if (!originalSetting) return;

    const hasChanged =
      setting.value !== originalSetting.value ||
      setting.isSensitiveData !== originalSetting.isSensitiveData;

    // Update the changedSettings array
    if (hasChanged) {
      // Remove if it already exists
      this.changedSettings = this.changedSettings.filter(s => s.key !== setting.key);
      // Add the changed setting
      this.changedSettings.push({ ...setting });
    } else {
      // Remove from changed settings if it's back to original
      this.changedSettings = this.changedSettings.filter(s => s.key !== setting.key);
    }
  }

  cancelEditing(setting: ApplicationSetting): void {
    if (this.editingKeys.has(setting.key)) {
      this.editingKeys.delete(setting.key);
    }

    for (let i = 0; i < this.changedSettings.length; i++) {
      if (this.changedSettings[i].key === setting.key) {
        this.changedSettings.splice(i, 1);
        break;
      }
    }

    for (const originalSetting of this.originalSettings) {
      if (originalSetting.key === setting.key) {
        setting.value = originalSetting.value;
      }
    }
  }

  isSettingChanged(key: string): boolean {
    return this.changedSettings.some(setting => setting.key === key);
  }

  discardChanges(): void {
    this.settings = this.originalSettings.map(setting => ({ ...setting }));
    this.changedSettings = [];
    this.editingKeys.clear();
    this.error = null;
    this.success = null;
  }

  openConfirmModal(): void {
    if (this.changedSettings.length === 0) {
      this.success = this.translateService.instant('APPLICATION_SETTINGS.MESSAGES.NO_CHANGES');
      return;
    }
    this.confirmModal.openDialog();
  }


  saveChanges(): void {
    if (this.changedSettings.length === 0) {
      this.success = this.translateService.instant('APPLICATION_SETTINGS.MESSAGES.NO_CHANGES');
      return;
    }

    this.loading = true;
    this.error = null;
    this.success = null;

    this.settingsService.updateSettings(this.changedSettings).subscribe({
      next: () => {
        this.success = this.translateService.instant('APPLICATION_SETTINGS.MESSAGES.UPDATE_SUCCESS');
        this.loading = false;
        this.confirmModal.closeDialog();
        this.loadSettings();
      },
      error: (err) => {
        this.error = this.translateService.instant('APPLICATION_SETTINGS.MESSAGES.UPDATE_ERROR');
        this.loading = false;
        this.confirmModal.closeDialog();
        console.error(err);
      }
    });
  }
}
